import React from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const ComparisonBar = ({ 
  selectedVendors = [], 
  maxComparisons = 3, 
  onRemoveVendor, 
  onClearAll, 
  onCompare, 
  onClose 
}) => {
  if (selectedVendors.length === 0) {
    return null;
  }

  const handleRemoveVendor = (vendorId) => {
    if (onRemoveVendor && typeof onRemoveVendor === 'function') {
      onRemoveVendor(vendorId);
    }
  };

  const handleClearAll = () => {
    if (onClearAll && typeof onClearAll === 'function') {
      onClearAll();
    }
  };

  const handleCompare = () => {
    if (onCompare && typeof onCompare === 'function' && selectedVendors.length >= 2) {
      onCompare();
    }
  };

  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-border shadow-strong">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3 sm:py-4">
          {/* Close Button - Mobile */}
          <button
            onClick={handleClose}
            className="lg:hidden w-8 h-8 rounded-full flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-surface-100 transition-smooth"
            aria-label="Close comparison bar"
          >
            <Icon name="X" size={18} color="currentColor" />
          </button>

          {/* Selected Vendors */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 lg:flex-none">
            <div className="hidden sm:block">
              <h3 className="font-heading font-semibold text-text-primary">
                Compare Vendors
              </h3>
              <p className="text-xs text-text-secondary">
                {selectedVendors.length}/{maxComparisons} selected
              </p>
            </div>
            
            <div className="flex items-center space-x-2 overflow-x-auto">
              {selectedVendors.map((vendor) => (
                <div
                  key={vendor?.id || Math.random()}
                  className="flex items-center space-x-2 bg-primary-50 rounded-lg px-2 py-1 sm:px-3 sm:py-2 flex-shrink-0"
                >
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full overflow-hidden">
                    <Image
                      src={vendor?.image || vendor?.vendor_gallery?.[0]?.image_url || '/assets/images/no_image.png'}
                      alt={vendor?.name || vendor?.business_name || 'Vendor'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/images/no_image.png';
                      }}
                    />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-primary truncate max-w-20 sm:max-w-none">
                    {vendor?.name || vendor?.business_name || 'Unknown Vendor'}
                  </span>
                  <button
                    onClick={() => handleRemoveVendor(vendor?.id)}
                    className="text-primary hover:text-primary-600 transition-colors p-1"
                    aria-label={`Remove ${vendor?.name || vendor?.business_name} from comparison`}
                  >
                    <Icon name="X" size={14} color="currentColor" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              iconName="RotateCcw"
              iconSize={14}
              className="hidden sm:inline-flex"
              disabled={selectedVendors.length === 0}
            >
              Clear
            </Button>
            
            <Button
              variant="primary"
              size="sm"
              onClick={handleCompare}
              disabled={selectedVendors.length < 2}
              iconName="BarChart3"
              iconSize={16}
              className="text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Compare</span>
              <span className="sm:hidden">Compare ({selectedVendors.length})</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonBar;